import { computed, defineComponent } from 'vue'
import type { ISwitchinputProps } from './types'
import { toBoolean } from '@/utils/boolean'

const Switchinput = defineComponent<Omit<ISwitchinputProps, 'value'> & {
  value: MaybeRef<boolean>,
  withText?: boolean;
}>({
  setup(props) {
    const checked = computed(() =>
      props.dataRef
        ? toBoolean(unref(props.dataRef.value.value))
        : toBoolean(unref(props.fieldRef?.value?.value || props.value))
    )

    const displayText = computed(() => {
      if (!props.valueText) {
        return checked.value ? 'Show' : 'Hide'
      }
      if (typeof props.valueText === 'function') {
        return props.valueText(checked.value)
      }
      return checked.value ? props.valueText.on : props.valueText.off
    })

    const handleClick = () => {
      if (props.disabled) return

      if (props.onToggle) {
        props.onToggle()
      }

      const nextValue = !checked.value;

      if (props.handleChange) {
        props.handleChange(nextValue);
      }

      if (props.reportChange && props.fieldRef) {
        props.reportChange(props.fieldRef, nextValue)
      }

      if (props.dataRef) {
        props.dataRef.setValue(nextValue)
      }

    }

    return () => {
      return (
        <div class={[
          props.withText ? "grid grid-cols-[1fr_max-content] items-center justify-between gap-2" : "grid items-center"
        ]}>
          <div
            class="relative inline-flex items-center cursor-pointer"
            onClick={handleClick}
            style={[
              {
                width: 'fit-content'
              },
              props.disabled || props.locked
                ? {
                  cursor: 'not-allowed',
                  opacity: 0.5
                }
                : {}
            ]}
          >
            <input
              type="checkbox"
              value={checked.value + ''}
              class="sr-only peer"
              checked={checked.value}
            />
            <div class="w-11 h-6 bg-gray-400 rounded-full peer peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:rtl:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:inset-s-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-primary-500"></div>
          </div>

          {
            props.withText && <div data-active={checked.value} class="grid items-center text-gray-400 data-[active=true]:text-primary-500 text-sm font-semibold transition-colors min-w-[38px]">
              {displayText.value}
            </div>
          }
        </div>
      )
    }
  }
})

Switchinput.props = [
  'name',
  'checked',
  'fieldRef',
  'value',
  'type',
  'asSwitch',
  'disabled',
  'locked',
  'reportChange',
  'onToggle',
  'tooltip',
  'dataRef',
  'handleChange',
  'withText',
  'valueText'
]

export default Switchinput
